import { type ApiClient, type ApiError, noContentSchema } from "@simpro/api-client";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { type ReactNode, useEffect } from "react";
import { z } from "zod";
import {
  type CurrentCustomer,
  currentCustomerSchema,
  type CustomerSession,
  customerSessionSchema,
} from "./schemas/auth.schemas";
import { createSessionCore } from "./session/sessionCore";
import { useAuthStore } from "./store/auth.store";

export interface CreateCustomerAuthOptions {
  apiClient: ApiClient;
  /** API prefix. Default "/api/v1". */
  basePath?: string;
  /** BroadcastChannel name shared by tabs of the same app. Default "simpro-customer-auth". */
  channelName?: string;
  /**
   * Runs before signing out, while the session still works: the booking app removes this phone's
   * push registration, so a shared device stops getting the last customer's reminders (push design
   * FR-NT-068). A failure here never keeps anyone signed in.
   */
  beforeSignOut?: () => Promise<void>;
}

export interface VerifyOtpInput {
  phone: string;
  code: string;
  /** What the customer sees in their device list. */
  deviceLabel?: string;
  /** The name typed beside the number (TDD-SA FR-SA-279): names a customer who has none yet. */
  displayName?: string;
}

export interface UpdateCustomerInput {
  displayName: string;
  /** Empty clears it. */
  email?: string | null;
}

export interface RequireCustomerProps {
  /** Rendered while a stored session is being restored. */
  fallback?: ReactNode;
  /** Rendered when nobody is signed in. The booking app shows its sign-in sheet rather than navigating away. */
  signedOut: ReactNode;
  children?: ReactNode;
}

export const customerAuthKeys = {
  all: ["customer-auth"] as const,
  me: () => [...customerAuthKeys.all, "me"] as const,
};

/** The request endpoint answers the same 202 for every number, so there is no body to read. */
const acceptedSchema = z.unknown();

/**
 * Sign-in for a salon's customers: a mobile number, a six-digit code, and no password anywhere
 * (TDD-SA FR-SA-070 to FR-SA-074).
 *
 * Separate from `createAuth` because the two principals share almost nothing above the token: a
 * customer holds no roles, has no password to change and no second factor to owe, and signs in with
 * something that expires in five minutes. What they do share is everything below it, which is
 * `createSessionCore`: one rotation, one cross-tab lease, one proactive refresh.
 *
 * The booking app and the operations app run on different hosts, so their storage never meets and
 * the two can safely use the same keys.
 */
export function createCustomerAuth(options: CreateCustomerAuthOptions) {
  const { apiClient } = options;
  const basePath = options.basePath ?? "/api/v1";
  const authPath = `${basePath}/customer-auth`;
  const store = useAuthStore;

  const core = createSessionCore({
    apiClient,
    refreshPath: `${authPath}/refresh`,
    sessionSchema: customerSessionSchema,
    channelName: options.channelName ?? "simpro-customer-auth",
  });

  const { applySession, refreshSession, signOutLocally } = core;

  async function runBeforeSignOut(): Promise<void> {
    await options.beforeSignOut?.().catch(() => undefined);
  }

  function currentCustomer(): CurrentCustomer | undefined {
    const session = store.getState().session;
    return session && "customer" in session ? session.customer : undefined;
  }

  /* ---------- hooks ---------- */

  /**
   * Asks for a code. Always succeeds as far as the caller can see, because the server answers the
   * same whether the number exists, so the app must not draw any conclusion from it either.
   */
  function useRequestOtp() {
    return useMutation<unknown, ApiError, { phone: string }>({
      meta: { silent: true },
      mutationFn: ({ phone }) =>
        apiClient.post(acceptedSchema, `${authPath}/otp/request`, { phone }, { noRefresh: true }),
    });
  }

  /** Exchanges the code for a session. This is the only way a customer account comes into existence. */
  function useVerifyOtp() {
    const queryClient = useQueryClient();
    return useMutation<CustomerSession, ApiError, VerifyOtpInput>({
      meta: { silent: true },
      mutationFn: ({ phone, code, deviceLabel, displayName }) =>
        apiClient.post(
          customerSessionSchema,
          `${authPath}/otp/verify`,
          { phone, code, deviceLabel, displayName: displayName?.trim() ? displayName.trim() : undefined },
          { noRefresh: true },
        ),
      onSuccess: (session) => {
        applySession(session, { broadcast: true });
        queryClient.setQueryData(customerAuthKeys.me(), session.customer);
      },
    });
  }

  function useSignOut() {
    const queryClient = useQueryClient();
    return useMutation<void, ApiError, void>({
      mutationFn: async () => {
        await runBeforeSignOut();
        // The server revokes the token its cookie carries and expires the cookie (ADR-0005). Always
        // called: the route is anonymous with the cookie as its proof, and only the server can see
        // whether a cookie is there to revoke.
        await apiClient
          .post(noContentSchema, `${authPath}/logout`, undefined, { noRefresh: true })
          .catch(() => undefined);
      },
      onSettled: () => {
        signOutLocally();
        queryClient.clear();
      },
    });
  }

  /**
   * Signs the customer out of every device, this one included (TDD-SA FR-SA-074): the server revokes
   * every chain of theirs. The local session goes too, whatever the server answered.
   */
  function useSignOutEverywhere() {
    const queryClient = useQueryClient();
    return useMutation<void, ApiError, void>({
      mutationFn: async () => {
        await runBeforeSignOut();
        await apiClient.post(noContentSchema, `${authPath}/logout-all`, undefined);
      },
      onSettled: () => {
        signOutLocally();
        queryClient.clear();
      },
    });
  }

  /** The customer's own name and email (TDD-SA FR-SA-074). The phone is the account and never changes here. */
  function useUpdateCustomer() {
    const queryClient = useQueryClient();
    return useMutation<void, ApiError, UpdateCustomerInput>({
      meta: { silent: true },
      mutationFn: ({ displayName, email }) =>
        apiClient
          .put(noContentSchema, `${authPath}/me`, {
            displayName,
            email: email?.trim() ? email : null,
          })
          .then(() => undefined),
      onSuccess: () => queryClient.invalidateQueries({ queryKey: customerAuthKeys.me() }),
    });
  }

  function useCurrentCustomer() {
    const status = useAuthStore((s) => s.status);
    const fromSession = useAuthStore((s) =>
      s.session && "customer" in s.session ? s.session.customer : undefined,
    );
    return useQuery<CurrentCustomer, ApiError>({
      queryKey: customerAuthKeys.me(),
      queryFn: () => apiClient.get(currentCustomerSchema, `${authPath}/me`),
      enabled: status === "authenticated",
      initialData: fromSession,
      staleTime: 5 * 60_000,
    });
  }

  function useIsSignedIn(): boolean {
    return useAuthStore((s) => s.status === "authenticated" && s.session != null);
  }

  /**
   * Guards the parts of the booking app that need a customer. Renders `signedOut` in place rather
   * than navigating: browsing a salon is the point of the app, and being sent to a sign-in page for
   * looking at your own appointments would read as being thrown out.
   */
  function RequireCustomer({ fallback = null, signedOut, children }: RequireCustomerProps) {
    const status = useAuthStore((s) => s.status);

    useEffect(() => {
      if (status === "idle") store.getState().rehydrate();
      // A restore that could not be tried keeps the session and retries on its own.
      if (status === "restoring") refreshSession().catch(() => undefined);
    }, [status]);

    if (status === "authenticated") return <>{children}</>;
    if (status === "anonymous") return <>{signedOut}</>;
    return <>{fallback}</>;
  }

  return {
    refreshSession,
    ensureSession: core.ensureSession,
    signOutLocally,
    currentCustomer,
    useRequestOtp,
    useVerifyOtp,
    useSignOut,
    useSignOutEverywhere,
    useUpdateCustomer,
    useCurrentCustomer,
    useIsSignedIn,
    RequireCustomer,
    customerAuthKeys,
  };
}

export type CustomerAuth = ReturnType<typeof createCustomerAuth>;
